import { db } from '../../firebase';
import { Lesson, PollStats } from '../types/lesson';
import {
  collection,
  getDocs,
  addDoc,
  updateDoc,
  doc,
  where,
  query,
  documentId,
  getDoc,
  deleteField,
} from 'firebase/firestore';
import { parseContent, parseContents } from '../utils';
import { parseLesson, parseLessons } from './parser';
import { ProjectErrorType } from '../../utils/errors/types';
import { getProjectErrorFromRawError, isProjectError } from '../../utils/errors';
import { CreateLessonDto, UpdateLessonDto } from '../typeDtos/lesson';
import _ from 'lodash';

export const getAllLessons = async ({
  category,
  pillarType,
  pillarExpertise,
  lessonType,
}: {
  category?: string;
  pillarType?: string;
  pillarExpertise?: string;
  lessonType?: string;
}): Promise<Lesson[]> => {
  try {
    const constraints = [];
    category && constraints.push(where('category', '==', category));
    pillarType && constraints.push(where('pillarType', '==', pillarType));
    pillarExpertise && constraints.push(where('pillarExpertise', '==', pillarExpertise));
    switch (lessonType) {
      case 'dailyAlux':
        constraints.push(where('isDailyLesson', '==', true));
        break;
      case 'ragsToRichesStory':
        constraints.push(where('isRagsToRichesStory', '==', true));
        break;
      case 'sleepStory':
        constraints.push(where('isSleepStory', '==', true));
        break;
      case 'specialStory':
        constraints.push(where('isSpecialStory', '==', true));
        break;
      case 'wisdomStory':
        constraints.push(where('isWisdomStory', '==', true));
        break;
    }

    const _query = query(collection(db, 'lessons'), ...constraints);
    const rawResult = await getDocs(_query);
    return await parseLessons(parseContents(rawResult));
  } catch (error) {
    console.log('[LESSONS] Error at getAllLessons:', error);
    return [];
  }
};

export const getLessonById = async (id: string) => {
  try {
    const _query = await getDoc(doc(db, 'lessons', id));
    if (!_query.exists()) {
      console.log('No such document!');
      return undefined;
    }
    const result = await parseLesson(parseContent(_query));
    if (result && !isProjectError(result)) {
      return result as Lesson;
    }
    return undefined;
  } catch (error) {
    console.log(`[LESSONS] Error at getLessonById with id ${id}:`, error);
    return undefined;
  }
};
export const getLessonsByIds = async (ids: string[]): Promise<Lesson[]> => {
  if (!ids || (ids && ids.length === 0)) {
    return [];
  }
  try {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const promises: Promise<any>[] = [];
    _.chunk(ids, 10).forEach((t) => {
      const docs = getDocs(query(collection(db, 'lessons'), where(documentId(), 'in', t)));
      promises.push(docs);
    });
    const data = await Promise.all(promises);

    const results: { [key: string]: unknown } = {};
    if (data && Array.isArray(data)) {
      for (const t of data) {
        const parsedLessons = await parseLessons(parseContents(t)).then();
        parsedLessons.forEach((t) => {
          const key = t?.id;
          if (key) {
            results[key] = t;
          }
        });
      }
    }

    return ids
      .map((id: string) => {
        return results?.[id];
      })
      .filter((t) => t) as Lesson[];
  } catch (error) {
    console.log('[LESSONS] Error at getLessonsByIds', error);
    return [];
  }
};

export const getCalendarLessons = async (startDate: Date, endDate: Date): Promise<Lesson[]> => {
  try {
    const startDateTs = startDate?.getTime();
    const endDateTs = endDate?.getTime();

    const _query = query(
      collection(db, 'lessons'),
      where('publishedAt', '>=', startDateTs),
      where('publishedAt', '<=', endDateTs),
      where('isDailyLesson', '==', true),
    );
    const rawResult = await getDocs(_query);
    return await parseLessons(parseContents(rawResult));
  } catch (error) {
    console.log('[LESSONS] Error at getCalendarLessons:', error);
    return [];
  }
};

// The modals send `null` for a field the admin cleared, and `undefined` for one they never touched.
// Firestore rejects `undefined` and stores `null` literally, so neither is safe to write as-is.
const isEmpty = (value: unknown) => value === null || value === undefined;

// For creates: drop empty fields entirely, since there is no stored value to clear.
const omitEmptyFields = <T extends object>(data: T) =>
  Object.fromEntries(Object.entries(data).filter(([, value]) => !isEmpty(value)));

// For updates: a cleared field must be removed from the document rather than set to `null`,
// so backend triggers keying off field presence don't see stale intent.
const clearEmptyFields = <T extends object>(data: T) =>
  Object.fromEntries(
    Object.entries(data)
      .filter(([, value]) => value !== undefined)
      .map(([key, value]) => [key, value === null ? deleteField() : value]),
  );

export const createLesson = async (
  data: CreateLessonDto,
): Promise<ProjectErrorType | { success: boolean; id: string }> => {
  try {
    const nowTs = new Date().getTime();
    // A new lesson has nothing stored yet, so there is nothing to delete - just omit the empty fields.
    const _data = { ...omitEmptyFields(data), createdAt: nowTs, updatedAt: nowTs };
    const result = await addDoc(collection(db, 'lessons'), _data);
    return { success: true, id: result.id };
  } catch (e) {
    console.log('[LESSONS] Error at createLesson:', e);
    return getProjectErrorFromRawError(e as Error);
  }
};

export const updateLesson = async (data: UpdateLessonDto): Promise<ProjectErrorType | { success: boolean }> => {
  try {
    const nowTs = new Date().getTime();
    const _data = { ...clearEmptyFields(data), updatedAt: nowTs };
    const docRef = doc(db, 'lessons', data?.id);
    await updateDoc(docRef, _data);
    return { success: true };
  } catch (e) {
    console.log('[LESSONS] Error at updateLesson:', e);
    return getProjectErrorFromRawError(e as Error);
  }
};

export const getPollStatsByLessonId = async (lessonId: string): Promise<PollStats | undefined> => {
  try {
    const _query = await getDoc(doc(db, 'pollStats', lessonId));
    if (!_query.exists()) {
      return undefined;
    }
    const data = _query.data();
    const counts = Array.isArray(data?.counts) ? (data.counts as number[]) : [];
    return { counts, total: typeof data?.total === 'number' ? data.total : 0 };
  } catch (error) {
    console.log(`[LESSONS] Error at getPollStatsByLessonId with id ${lessonId}:`, error);
    return undefined;
  }
};
